import {
  APIReferenceAttributes,
  ArdoqId,
  FilterInterfaceFilter,
  FormattingFilter,
  isConditionalFormatting,
} from '@ardoq/api-types';
import {
  GetCssClassNamesOption,
  type EnhancedScopeData,
} from '@ardoq/data-model';
import {
  filterClass,
  getFillAndStroke,
  referenceModelClassName,
  referenceTypeClassName,
} from '@ardoq/color-helpers';
import { NO_FILTER_CLASS_NAME } from '@ardoq/global-consts';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';
import { isReferenceIncludedInContextByFilter } from './isIncludedInContextByFilter';

const gerReferenceFormattingFilters = (
  conditionalFormatting: (FilterInterfaceFilter | FormattingFilter)[]
) =>
  conditionalFormatting.filter(
    formatting =>
      isConditionalFormatting(formatting) && formatting.affectReference
  );

const applyCssOptions = (color: string, options: GetCssClassNamesOption) =>
  options.useAsBackgroundStyle
    ? getFillAndStroke(color, options.useAsBackgroundStyle)
    : { fill: undefined, stroke: color };

const getReferenceFilterColor = (
  scopeData: EnhancedScopeData,
  referenceFormattingFilters: (FilterInterfaceFilter | FormattingFilter)[],
  reference: APIReferenceAttributes
) =>
  referenceFormattingFilters.length
    ? isReferenceIncludedInContextByFilter(
        reference,
        referenceFormattingFilters,
        scopeData
      ).color
    : null;

export const getReferenceCssColorsWithScopeData = (
  scopeData: EnhancedScopeData,
  conditionalFormatting: (FilterInterfaceFilter | FormattingFilter)[],
  referenceId: ArdoqId,
  options: GetCssClassNamesOption
) => {
  const reference = enhancedScopeDataOperations.getReference(
    scopeData,
    referenceId
  );
  if (!reference) return null;
  const referenceFormattingFilters = gerReferenceFormattingFilters(
    conditionalFormatting
  );

  const filterColor = getReferenceFilterColor(
    scopeData,
    referenceFormattingFilters,
    reference
  );

  if (filterColor) {
    return applyCssOptions(filterColor, options);
  }

  // if no reference formatting and no filter color, return the model color
  const referenceType =
    enhancedScopeDataOperations.getReferenceTypeByReferenceId(
      scopeData,
      referenceId
    );
  if (!referenceType) return null;

  return applyCssOptions(referenceType.color, options);
};

export const getReferenceCssClassNamesWithScopeData = (
  scopeData: EnhancedScopeData,
  conditionalFormatting: (FilterInterfaceFilter | FormattingFilter)[],
  referenceId: ArdoqId
) => {
  const reference = enhancedScopeDataOperations.getReference(
    scopeData,
    referenceId
  );
  if (!reference) {
    return null;
  }
  const cssColor = getReferenceCssColorsWithScopeData(
    scopeData,
    conditionalFormatting,
    referenceId,
    {
      useAsBackgroundStyle: false,
    }
  )?.stroke;

  const filterColorClassNames =
    filterClass(cssColor ?? NO_FILTER_CLASS_NAME) || NO_FILTER_CLASS_NAME;

  return `integration ${referenceModelClassName(
    reference._id ?? ''
  )} ${referenceTypeClassName(reference.type)} ${reference._id} ${filterColorClassNames}`;
};
